/**
 * Profile picture upload component with image compression.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useProfilePicture } from '../hooks/useProfilePicture';
import { useAuth } from '../../auth/hooks/useAuth';
import type { UserProfile } from '../types';

interface ProfilePictureUploadProps {
  profile: UserProfile | null;
  onUpdate: () => void;
}

const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = ({ profile, onUpdate }) => {
  const { uploadPicture, deletePicture, uploading, error: uploadError } = useProfilePicture();
  const { refreshProfile } = useAuth();
  const [preview, setPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (profile?.profile_picture_url) {
      setPreview(profile.profile_picture_url);
    } else {
      setPreview(null);
    }
  }, [profile?.profile_picture_url]);

  const compressImage = async (file: File): Promise<File> => {
    try {
      // Dynamic import to avoid issues if package not installed
      // @ts-ignore - Package may not be installed yet, but code handles this gracefully
      const imageCompression = await import('browser-image-compression');
      
      const options = {
        maxSizeMB: 0.3, // 300KB target
        maxWidthOrHeight: 800,
        useWebWorker: true,
        fileType: file.type,
      };

      const compressedFile = await imageCompression.default(file, options);
      return compressedFile;
    } catch (err) {
      // If compression fails or package not available, return original file
      console.warn('Image compression failed, using original file:', err);
      return file;
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a valid image file (JPG, PNG, or WebP)');
      return;
    }

    // Validate file size (5MB max before compression)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setError(null);
    setSelectedFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setError(null);
      const compressedFile = await compressImage(selectedFile);
      await uploadPicture(compressedFile);
      setSelectedFile(null);
      // Refresh both settings profile and auth context profile
      await refreshProfile();
      onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload picture');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete your profile picture?')) {
      return;
    }

    try {
      setError(null);
      await deletePicture();
      setPreview(null);
      setSelectedFile(null);
      // Refresh both settings profile and auth context profile
      await refreshProfile();
      onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete picture');
    }
  };

  const displayError = error || uploadError;

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-6">
        {/* Profile Picture Preview */}
        <div className="relative">
          <div className="h-24 w-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
            {preview ? (
              <img
                src={preview}
                alt="Profile preview"
                className="h-full w-full object-cover"
              />
            ) : (
              <svg
                className="h-12 w-12 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            )}
          </div>
        </div>

        {/* Upload Controls */}
        <div className="flex-1 space-y-2">
          <div className="flex items-center space-x-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              {uploading ? 'Uploading...' : 'Choose Photo'}
            </button>
            {preview && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={uploading}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                Remove
              </button>
            )}
            {selectedFile && !uploading && (
              <button
                type="button"
                onClick={handleUpload}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm font-medium"
              >
                Upload
              </button>
            )}
          </div>
          {displayError && (
            <p className="text-sm text-red-600">{displayError}</p>
          )}
          <p className="text-xs text-gray-500">
            JPG, PNG or WebP. Max 5MB.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProfilePictureUpload;

